import { useState } from 'react'
import { useAdminAuth } from '../../shared/auth/admin-auth-context'
import { Kpi } from '../../shared/ui/charts/kpi'
import { daysBetween, formatNumber } from '../../shared/ui/charts/scale'
import { ActivitySection } from './activity-section'
import { FunnelSection } from './funnel-section'
import { LlmSection } from './llm-section'
import { PERIODS, useActivityStats, useFunnelStats, useLlmStats, useOverviewStats } from './queries'
import type { PeriodDays } from './queries'
import { UsersSection } from './users-section'
import styles from './overview.module.css'

export function OverviewPage() {
  const { user, status } = useAdminAuth()
  const isAdmin = status === 'authenticated' && (user?.role === 'admin' || user?.role === 'expert')
  const [period, setPeriod] = useState<PeriodDays>(30)
  const overview = useOverviewStats(period, isAdmin)
  const activity = useActivityStats(period, isAdmin)
  const funnel = useFunnelStats(period, isAdmin)
  const llm = useLlmStats(period, isAdmin)

  if (!isAdmin) return <p className="error-box">Раздел доступен только сотрудникам.</p>

  const range = overview.data?.period
  const days = range ? daysBetween(range.from, range.to) : []
  const failed = [overview, activity, funnel, llm].some((q) => q.isError)

  return (
    <div>
      <header className={styles.head}>
        <div>
          <h1>Обзор</h1>
          <p className="muted">Пользователи, активность, воронка и расходы на LLM. Время московское.</p>
        </div>
        <div className={styles.periods} role="radiogroup" aria-label="Период">
          {PERIODS.map((p) => (
            <button
              key={p}
              role="radio"
              aria-checked={period === p}
              className={period === p ? styles.periodActive : styles.period}
              onClick={() => setPeriod(p)}
            >
              {p} дней
            </button>
          ))}
        </div>
      </header>

      {failed && <p className="error-box">Часть данных не загрузилась, обновите страницу.</p>}
      {overview.isPending && <div className="spinner" aria-label="Загрузка" />}

      {overview.data && (
        <div className={styles.kpis}>
          <Kpi label="Пользователей" value={formatNumber(overview.data.identified)} hint={`с гостями ${formatNumber(overview.data.total)}`} />
          <Kpi label="Новых за 7 дней" value={formatNumber(overview.data.new_7d)} hint={`за 30 дней ${formatNumber(overview.data.new_30d)}`} />
          <Kpi label="DAU сегодня" value={activity.data ? formatNumber(activity.data.dau.at(-1)?.count ?? 0) : '…'} />
          <Kpi label="WAU" value={activity.data ? formatNumber(activity.data.wau) : '…'} />
          <Kpi label="MAU" value={activity.data ? formatNumber(activity.data.mau) : '…'} />
          <Kpi label="Заблокировано" value={formatNumber(overview.data.blocked)} />
        </div>
      )}

      {overview.data && <UsersSection data={overview.data} days={days} />}
      {activity.data && <ActivitySection data={activity.data} days={days} />}
      {funnel.data && <FunnelSection data={funnel.data} />}
      {llm.data && <LlmSection data={llm.data} days={days} />}
    </div>
  )
}
